import { useEffect, useState } from 'react'
import Head from 'next/head'
import Layout, { Breadcrumb } from 'components/layout'
import { Container, Link } from 'components/ui'
import type { FC } from 'react'
import { FourInlineCards } from 'components/cards'
import { ProductCarousel } from 'modules/products/components'
import { Product } from 'framework/products'
import parser from 'fazendinha/product'
import sellerParser from 'fazendinha/seller'
import { ProductService } from 'modules/products/services'
import { ProductCard, ProductGridWithSeller } from 'modules/products/containers'
import { PostSection } from 'modules/posts/components'
import { Seller } from 'framework/sellers'
import { SellerService } from 'modules/sellers/services'
import { ROUTE_PRODUCT_INDEX } from 'routes/products'
import { NextSeo } from 'next-seo'
import { useMercadinho } from 'hooks/use-mercadinho'
import s from './home.module.css'

interface HomeProps {}

type Products = Array<Product>

const Home: FC<HomeProps> = () => {
  const [seller, setSeller] = useState<Seller>(null as unknown as Seller)
  const [bestSellers, setBestSellers] = useState<Products>([] as Products)
  const [mostRecent, setMostRecent] = useState<Products>([] as Products)
  const [featured, setFeatured] = useState<Products>([] as Products)
  const { setIsMercadinho } = useMercadinho()

  function getProductsBestSellers() {
    const productService = new ProductService()
    return productService.getBestSellers()
  }

  function getProductsMostRecent() {
    const productService = new ProductService()
    return productService.getMostRecent()
  }

  function getProductsFeatured() {
    const productService = new ProductService()
    return productService.getFeatured()
  }

  function getSeller() {
    const sellerService = new SellerService()
    return sellerService.getFeaturedSeller()
  }

  useEffect(() => {
    setIsMercadinho(false)
    getProductsFeatured().then(({ data }: { data: any[] }) => {
      setFeatured(data.sort(() => Math.random() - 0.5).map((product) => parser(product)))
    })

    getProductsMostRecent().then(({ data }: { data: any[] }) => {
      setMostRecent(data.sort(() => Math.random() - 0.5).map((product) => parser(product)))
    })

    getProductsBestSellers().then(({ data }: { data: any[] }) => {
      setBestSellers(data.sort(() => Math.random() - 0.5).map((product) => parser(product)))
    })

    getSeller().then(({ data }: { data: any }) => {
      setSeller(sellerParser(data[0]))
    })
  }, [])

  return (
    <Layout>
      <Head>
        <NextSeo
          title="Fazendinha em Casa • Produtos frescos, direto do produtor para você!"
          description="Na Fazendinha você compra produtos locais direto do produtor. Produtos naturais, sem agrotóxicos para você que quer ter uma alimentação saudável."
          openGraph={{
            type: 'website',
            title: 'Fazendinha em Casa • Produtos frescos, direto do produtor para você!',
            description:
              'Na Fazendinha você compra produtos locais direto do produtor. Produtos naturais, sem agrotóxicos para você que quer ter uma alimentação saudável.',
          }}
        />
      </Head>
      <Breadcrumb crumbs={[{ label: 'Home' }]} isHome />
      <div className={s.root}>
        <Container>
          <div className={s.firstTitle}>Bem-vindo ao seu mercado online de produtores</div>
          <div className={s.subtitle}>Aqui você cultiva um mundo melhor</div>
          <div
            id="carouselExampleCaptions"
            className={`carousel slide relative ${s.twCarousel}`}
            data-bs-ride="carousel"
          >
            <div
              className={`carousel-indicators absolute bottom-0 left-0 right-0 flex justify-center ${s.buttonsIndicator}`}
            >
              <button
                type="button"
                data-bs-target="#carouselExampleCaptions"
                data-bs-slide-to="0"
                className="active"
                aria-current="true"
                aria-label="Slide 1"
              />
              <button
                type="button"
                data-bs-target="#carouselExampleCaptions"
                data-bs-slide-to="1"
                aria-label="Slide 2"
              />
              <button
                type="button"
                data-bs-target="#carouselExampleCaptions"
                data-bs-slide-to="2"
                aria-label="Slide 3"
              />
              <button
                type="button"
                data-bs-target="#carouselExampleCaptions"
                data-bs-slide-to="3"
                aria-label="Slide 4"
              />
            </div>
            <div className="carousel-inner relative w-full overflow-hidden">
              <div className="carousel-item active relative float-left w-full">
                <Link href="/produtos/fora-da-curva">
                  <img src="/assets/images/14-04/bannerForaDaCurva.jpg" alt="Fora da curva" className="block w-full" />
                </Link>
              </div>
              <div className="carousel-item relative float-left w-full">
                <Link href={ROUTE_PRODUCT_INDEX}>
                  <img src="/assets/images/14-04/bannerOne.jpg" alt="Produtos organicos" className="block w-full" />
                </Link>
              </div>
              <div className="carousel-item relative float-left w-full">
                <Link href="/seja-um-vendedor">
                  <img src="/assets/images/14-04/bannerTwo.jpg" alt="Abra ja sua loja" className="block w-full" />
                </Link>
              </div>
              <div className="carousel-item relative float-left w-full">
                <Link href={`${ROUTE_PRODUCT_INDEX}experiencias`}>
                  <img
                    src="/assets/images/14-04/bannerThree.jpg"
                    alt="vivencie experiencias"
                    className="block w-full"
                  />
                </Link>
              </div>
            </div>
            <button
              className="carousel-control-prev absolute bottom-0 left-0 top-0 flex items-center justify-center p-0 text-center hover:no-underline focus:no-underline border-0 hover:outline-none focus:outline-none"
              type="button"
              data-bs-target="#carouselExampleCaptions"
              data-bs-slide="prev"
            >
              <span className="carousel-control-prev-icon inline-block bg-no-repeat" aria-hidden="true" />
              <span className="visually-hidden">Previous</span>
            </button>
            <button
              className="carousel-control-next absolute bottom-0 right-0 top-0 flex items-center justify-center p-0 text-center hover:no-underline focus:no-underline border-0 hover:outline-none focus:outline-none"
              type="button"
              data-bs-target="#carouselExampleCaptions"
              data-bs-slide="next"
            >
              <span className="carousel-control-next-icon inline-block bg-no-repeat" aria-hidden="true" />
              <span className="visually-hidden">Next</span>
            </button>
          </div>
          <FourInlineCards
            cards={[
              {
                icon: <img src="/assets/icons/inline-cards/farm-products.svg" alt="" width="60px" />,
                title: 'Produtos Fresquinhos',
                description: '',
              },
              {
                icon: <img src="/assets/icons/inline-cards/farm-tractor.svg" alt="" width="60px" />,
                title: 'Frete único de baixo custo',
                description: '',
              },
              {
                icon: <img src="/assets/icons/inline-cards/milk-products.svg" alt="" width="60px" />,
                title: 'Produtores selecionados',
                description: '',
              },
              {
                icon: <img src="/assets/icons/inline-cards/produtor.svg" alt="" width="60px" />,
                title: 'Hortifruti sem agrotóxicos',
                description: '',
              },
            ]}
          />
          <div className={s.banners}>
            <Link href={`${ROUTE_PRODUCT_INDEX}hortifruti`}>
              <img src="/assets/images/26-07/01hortifruti.jpg" alt="Horti & Fruti" />
            </Link>
            <Link href={`${ROUTE_PRODUCT_INDEX}laticinios`}>
              <img src="/assets/images/26-07/02Laticinios.jpg" alt="Laticinios" />
            </Link>
            <Link href={`${ROUTE_PRODUCT_INDEX}carnes-e-ovos`}>
              <img src="/assets/images/26-07/03carnes.jpg" alt="Carnes & ovos" />
            </Link>
          </div>
          <div className={s.banners}>
            <Link href={`${ROUTE_PRODUCT_INDEX}emporio`}>
              <img src="/assets/images/26-07/04emporio.jpg" alt="Emporio" />
            </Link>
            <Link href={`${ROUTE_PRODUCT_INDEX}bebidas`}>
              <img src="/assets/images/26-07/05bebidas.jpg" alt="Bebidas" />
            </Link>
            <Link href={`${ROUTE_PRODUCT_INDEX}padaria-e-confeitaria`}>
              <img src="/assets/images/26-07/06padaria.jpg" alt="Padaria & Confeitaria" />
            </Link>
          </div>
          <div className={s.banners}>
            <Link href={`${ROUTE_PRODUCT_INDEX}mel-e-colmeia`}>
              <img src="/assets/images/26-07/07melecolmeia.jpg" alt="Mel e colméia" />
            </Link>
            <Link href={`${ROUTE_PRODUCT_INDEX}congelados`}>
              <img src="/assets/images/26-07/08congelados.jpg" alt="Congelados" />
            </Link>
            <Link href={`${ROUTE_PRODUCT_INDEX}cuidados-pessoais`}>
              <img src="/assets/images/26-07/09cuidadospessoais.jpg" alt="Cuidados pessoais" />
            </Link>
          </div>
          <div className={s.banners}>
            <Link href={`${ROUTE_PRODUCT_INDEX}coisas-de-casa`}>
              <img src="/assets/images/26-07/10coisasdecasa.jpg" alt="Coisas de Casa" />
            </Link>
            <Link href={`${ROUTE_PRODUCT_INDEX}jardim-e-quintal`}>
              <img src="/assets/images/26-07/11jardimequintal.jpg" alt="Jardim e Quintal" />
            </Link>
            <Link href={`${ROUTE_PRODUCT_INDEX}experiencias`}>
              <img src="/assets/images/26-07/12experiencias.jpg" alt="Experiências" />
            </Link>
          </div>

          <div className={s.products}>
            <div className={s.title}>Os mais desejados</div>
            <ProductCarousel key="product-section-featured">
              {featured.map((product) => (
                <ProductCard key={`product-section-featured-${product.id}`} product={product} />
              ))}
            </ProductCarousel>
          </div>

          <div className={s.products}>
            <div className={s.title}>Ofertas em destaque</div>
            <ProductCarousel key="product-section-best-sellers">
              {bestSellers.map((product) => (
                <ProductCard key={`product-section-best-sellers-${product.id}`} product={product} />
              ))}
            </ProductCarousel>
          </div>
        </Container>

        <Container>
          <div className={s.products}>
            <div className={s.title}>Seleção revendedor & foodservice</div>
            <ProductCarousel key="product-section-most-recent">
              {mostRecent.map((product) => (
                <ProductCard key={`product-section-most-recent-${product.id}`} product={product} />
              ))}
            </ProductCarousel>
          </div>
        </Container>
        <div className={s.content}>
          <Container>{seller && seller.id && <ProductGridWithSeller seller={seller} />}</Container>
        </div>
        <Container className={s.blog}>
          <div className={s.title}>Blog da Fazendinha</div>
          <br />
          <PostSection ignore="" limit={3} />
        </Container>
      </div>
    </Layout>
  )
}

export default Home
